import React from "react";
import { useParams, NavLink } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, Kpi, CardSkeleton, StatusBadge, TabList, EmptyState, ResponsiveTable, DateCell, Modal, CopyId } from "@/admin/ui";
import { Coins, Snowflake, ShieldAlert, Plus, LogOut } from "lucide-react";

const fmt$ = (cents) => `$${((Number(cents || 0)) / 100).toFixed(2)}`;

const TABS = ["overview", "credits", "orders", "subscriptions", "api_keys", "refunds", "disputes", "notes", "audit"];

export default function AdminCustomer() {
  const { user_id } = useParams();
  const { t } = useLang();
  const [tab, setTab] = React.useState("overview");
  const [data, setData] = React.useState(null);
  const [dialog, setDialog] = React.useState(null);

  const load = React.useCallback(() => {
    setData(null);
    api.get(`/api/admin/customers/${user_id}`).then((r) => setData(r.data));
  }, [user_id]);
  React.useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="space-y-6" data-testid="admin-customer-page">
      <PageHeader breadcrumbs={[{ label: t("admin.users.title"), to: "/admin/users" }, { label: t("admin.customer.back") }]} title="…" />
      <CardSkeleton h="h-40" />
    </div>
  );
  const u = data.user;
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="admin-customer-page">
      <PageHeader
        breadcrumbs={[{ label: t("admin.users.title"), to: "/admin/users" }, { label: u.email }]}
        title={u.name || u.email}
        description={<span>{u.email} · <CopyId id={u.id} label="User ID" chars={12} /></span>}
        badge={<div className="flex flex-wrap gap-1">
          {u.active === false && <StatusBadge tone="danger">{t("admin.users.suspended")}</StatusBadge>}
          {u.topup_frozen && <StatusBadge tone="warning" icon={Snowflake}>{t("admin.users.frozen")}</StatusBadge>}
          {u.role === "admin" && <StatusBadge tone="brand">Admin</StatusBadge>}
          {u.active !== false && !u.topup_frozen && u.role !== "admin" && <StatusBadge tone="success">Active</StatusBadge>}
        </div>}
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={() => setDialog({ kind: "credit" })} data-testid="btn-credit-adjust">
          <Coins className="w-3.5 h-3.5" /> {t("admin.customer.credit_adjust")}
        </button>}
        secondary={<button className="lp-btn-secondary text-xs py-1.5 px-3"
          onClick={() => setDialog({ kind: u.topup_frozen ? "unfreeze" : "freeze" })} data-testid="btn-freeze">
          <Snowflake className="w-3.5 h-3.5" /> {u.topup_frozen ? t("admin.customer.unfreeze") : t("admin.customer.freeze")}
        </button>}
        tertiary={<button className="lp-btn-secondary text-xs py-1.5 px-3"
          onClick={() => setDialog({ kind: u.active === false ? "unsuspend" : "suspend" })} data-testid="btn-suspend">
          <ShieldAlert className="w-3.5 h-3.5" /> {u.active === false ? t("admin.customer.unsuspend") : t("admin.customer.suspend")}
        </button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label={t("admin.customer.k.credits")} value={k.credits_balance.toLocaleString()} />
        <Kpi label={t("admin.customer.k.gross")} value={fmt$(k.lifetime_gross_cents)} />
        <Kpi label={t("admin.customer.k.refunded")} value={fmt$(k.lifetime_refunded_cents)} tone={k.lifetime_refunded_cents ? "danger" : undefined} />
        <Kpi label={t("admin.customer.k.net")} value={fmt$(k.lifetime_net_cents)} tone="success" />
        <Kpi label={t("admin.customer.k.orders")} value={k.orders_count} />
        <Kpi label={t("admin.customer.k.disputes")} value={k.open_disputes} tone={k.open_disputes ? "danger" : undefined} />
      </div>

      <TabList value={tab} onChange={setTab} tabs={TABS.map((id) => ({ id, label: t(`admin.customer.tab.${id}`) }))} />

      <SectionCard>
        {tab === "overview" && (
          <div className="text-sm text-white/70 space-y-2">
            <div>{t("admin.customer.overview.joined")}: <span className="text-white/90">{new Date(u.created_at).toLocaleString()}</span></div>
            <div>{t("admin.customer.overview.language")}: <span className="text-white/90">{u.language}</span></div>
            {u.creem_customer_id && <div>Creem customer: <span className="font-mono text-white/90">{u.creem_customer_id}</span></div>}
            {u.topup_frozen_reason && <div className="text-amber-300">{t("admin.customer.overview.freeze_reason")}: {u.topup_frozen_reason}</div>}
          </div>
        )}
        {tab !== "overview" && <ListTable tab={tab} data={data} onNote={() => setDialog({ kind: "note" })} />}
      </SectionCard>

      {dialog && <CustomerDialog dialog={dialog} user={u} onClose={() => setDialog(null)} onDone={load} />}
    </div>
  );
}

function ListTable({ tab, data, onNote }) {
  const { t } = useLang();
  const rows = tab === "orders" ? data.invoices
    : tab === "credits" ? data.ledger
    : tab === "subscriptions" ? data.subscriptions
    : tab === "api_keys" ? data.api_keys
    : tab === "refunds" ? data.refunds
    : tab === "disputes" ? data.disputes
    : tab === "notes" ? data.notes
    : data.audits;

  if (tab === "notes") return (
    <div className="space-y-3">
      <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={onNote} data-testid="btn-add-note">
        <Plus className="w-3.5 h-3.5" /> {t("admin.customer.notes.add")}
      </button>
      {rows.length === 0 ? <EmptyState title="No notes yet" /> :
        rows.map((n) => (
          <div key={n.id} className="border-l-2 border-amber-500/40 pl-3 py-1">
            <div className="text-white/85 text-sm whitespace-pre-wrap">{n.body}</div>
            <div className="text-white/40 text-xs">{n.author_email} · {new Date(n.created_at).toLocaleString()} · {n.category}</div>
          </div>
        ))
      }
    </div>
  );
  if (rows.length === 0) return <EmptyState title="Nothing to show" />;
  if (tab === "credits") return (
    <ResponsiveTable minWidth={700}>
      <thead className="lp-table-sticky"><tr><th>When</th><th>Reason</th><th className="text-right">Δ</th><th className="text-right">Balance</th><th>Reference</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id}>
          <td><DateCell value={r.created_at} /></td>
          <td className="text-xs">{r.reason}</td>
          <td className={`text-right tabular-nums ${r.delta < 0 ? "text-red-300" : "text-emerald-300"}`}>{r.delta > 0 ? "+" : ""}{r.delta}</td>
          <td className="text-right text-white/70 tabular-nums">{r.balance_after?.toLocaleString?.() || "—"}</td>
          <td className="font-mono text-white/50 text-[10px] truncate max-w-[220px]">{r.reference_id}</td>
        </tr>))}</tbody>
    </ResponsiveTable>
  );
  if (tab === "orders") return (
    <ResponsiveTable minWidth={700}>
      <thead className="lp-table-sticky"><tr><th>#</th><th>Date</th><th>Product</th><th className="text-right">Total</th><th>Status</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id}>
          <td className="font-mono text-xs">{r.invoice_number || r.number}</td>
          <td><DateCell value={r.created_at} mode="date" /></td>
          <td className="text-xs">{r.product_name}</td>
          <td className="text-right tabular-nums">${((r.total_cents || 0) / 100).toFixed(2)}</td>
          <td className="text-xs">{r.status}</td>
        </tr>
      ))}</tbody>
    </ResponsiveTable>
  );
  return (
    <div className="space-y-1 text-xs">
      {rows.map((r) => (
        <div key={r.id} className="border-b border-white/5 pb-1 flex justify-between gap-2">
          <span className="text-white/70 truncate">{r.action || r.event_type || r.creem_refund_id || r.creem_dispute_id || r.creem_subscription_id || r.key_prefix || r.id}</span>
          <span className="text-white/40 shrink-0"><DateCell value={r.created_at} /></span>
        </div>
      ))}
    </div>
  );
}

function CustomerDialog({ dialog, user, onClose, onDone }) {
  const { t } = useLang();
  const [reason, setReason] = React.useState("");
  const [delta, setDelta] = React.useState("");
  const [category, setCategory] = React.useState("admin_adjustment");
  const [note, setNote] = React.useState("");
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      if (dialog.kind === "credit") {
        const r = await api.post(`/api/admin/customers/${user.id}/credit-adjust`, { delta: Number(delta), category, reason, note });
        toast.success(`New balance: ${r.data.new_balance.toLocaleString()}`);
      } else if (dialog.kind === "note") {
        await api.post(`/api/admin/customers/${user.id}/notes`, { category: "general", body });
        toast.success(t("admin.customer.notes.added"));
      } else if (dialog.kind === "freeze") { await api.post(`/api/admin/customers/${user.id}/freeze-topup`, { reason }); toast.success(t("admin.customer.freeze_done")); }
      else if (dialog.kind === "unfreeze") { await api.post(`/api/admin/customers/${user.id}/unfreeze-topup`, { reason }); toast.success(t("admin.customer.unfreeze_done")); }
      else if (dialog.kind === "suspend") { await api.post(`/api/admin/customers/${user.id}/suspend`, { reason }); toast.success(t("admin.customer.suspend_done")); }
      else if (dialog.kind === "unsuspend") { await api.post(`/api/admin/customers/${user.id}/unsuspend`, { reason }); toast.success(t("admin.customer.unsuspend_done")); }
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const prev = user.credits || 0;
  const newBal = dialog.kind === "credit" && delta !== "" ? prev + Number(delta) : null;
  const canSubmit = dialog.kind === "note" ? body.length > 0
    : dialog.kind === "credit" ? (delta !== "" && reason.length >= 3)
    : reason.length >= 3;

  return (
    <Modal open onClose={onClose} title={t(`admin.customer.dialog.${dialog.kind}`)} testid={`dialog-${dialog.kind}`}
      footer={<>
        <button className="lp-btn-secondary" onClick={onClose} disabled={busy}>{t("admin.customer.cancel")}</button>
        <button className="lp-btn-primary" onClick={apply} disabled={busy || !canSubmit} data-testid="dialog-submit">
          {busy ? "…" : t("admin.customer.confirm")}
        </button>
      </>}>
      {dialog.kind === "credit" && (
        <>
          <label className="block space-y-1">
            <span className="text-xs text-white/60">{t("admin.customer.delta")}</span>
            <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="lp-input w-full" data-testid="credit-delta" data-autofocus />
          </label>
          {newBal !== null && (
            <div className="text-xs text-white/60 rounded-lg bg-white/5 border border-white/10 p-2">
              This is a compensating ledger entry, not a direct balance write. Projected: {prev.toLocaleString()} → <span className={newBal < 0 ? "text-red-300" : "text-emerald-300"}>{newBal.toLocaleString()}</span>
            </div>
          )}
          <label className="block space-y-1">
            <span className="text-xs text-white/60">{t("admin.customer.category")}</span>
            <input value={category} onChange={(e) => setCategory(e.target.value)} className="lp-input w-full" data-testid="credit-category" />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-white/60">{t("admin.customer.note")}</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="lp-input w-full" data-testid="credit-note" />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-white/60">{t("admin.customer.reason")}</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="lp-input w-full min-h-[80px]" data-testid="dialog-reason" />
          </label>
        </>
      )}
      {dialog.kind === "note" && (
        <label className="block space-y-1">
          <span className="text-xs text-white/60">{t("admin.customer.notes.body")}</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} className="lp-input w-full min-h-[100px]" data-testid="note-body" data-autofocus />
        </label>
      )}
      {dialog.kind !== "note" && dialog.kind !== "credit" && (
        <label className="block space-y-1">
          <span className="text-xs text-white/60">{t("admin.customer.reason")}</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="lp-input w-full min-h-[100px]" data-testid="dialog-reason" data-autofocus />
        </label>
      )}
    </Modal>
  );
}
